import Button from "@/components/button"
import Checkbox from "@/components/checkbox"
import ListItem from "@/components/list-item"
import Modal from "@/components/modal"
import SearchBar from "@/components/search-bar"
import Space from "@/components/space"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import { userActivate } from "@/redux/user.actions"
import { User } from "@/redux/user.types"
import { faUserPlus } from "@fortawesome/free-solid-svg-icons"
import { useMemo, useState } from "react"
import PreventionActionResponsibleButton from "../PreventionActionResponsibleButton"

interface Props {
  value: string | null
  zIndex?: number //Ouvert depuis un panneau lateral, qui passe deja au-dessus des modales
  onClose: () => void
  onSelect: (userId: string | null) => void
}

export default function PreventionActionResponsibleModal({ value, zIndex, onClose, onSelect }: Props) {
  const dispatch = useAppDispatch()
  const users = useAppSelector((state) => state.user.list)
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const [search, setSearch] = useState("")
  const [onlyEstablishment, setOnlyEstablishment] = useState(true)
  const [modal, setModal] = useState<null | "MODAL_USER_ADD">(null)
  const establishmentId = activeEstablishment?.id ?? null

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return users
      .filter((user) => {
        if (!onlyEstablishment) return true
        return user.EstablishmentId === establishmentId || (user.role === "ADMIN" && !user.EstablishmentId)
      })
      .filter((user) => {
        if (!normalizedSearch) return true

        return [user.username, user.email, user.firstname, user.lastname]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch))
      })
  }, [users, search, onlyEstablishment, establishmentId])

  function select(userId: string | null) {
    const user = users.find((item) => item.id === userId)
    if (user) {
      dispatch(userActivate(user))
    }

    onSelect(userId)
    onClose()
  }

  function selectCreatedUser(user: User) {
    dispatch(userActivate(user))
    onSelect(user.id)
    onClose()
  }

  return (
    <Modal title="Définir responsable" isLarge isCloseButtonVisible zIndex={zIndex} onClose={onClose}>
      {modal === "MODAL_USER_ADD" && (
        <AdmEditUserModal
          allowFromSameAccount
          zIndex={zIndex ? zIndex + 1 : undefined}
          userDefaults={{
            EstablishmentId: establishmentId
          }}
          onClose={() => setModal(null)}
          onNext={selectCreatedUser}
        />
      )}

      <div className="flex">
        <SearchBar searchText={search} onSearch={(value) => setSearch(value)} />
        <Space />
        <Button icon={faUserPlus} className="secondary" isWithBorder onClick={() => setModal("MODAL_USER_ADD")}>
          Ajouter un utilisateur
        </Button>
      </div>

      <div className="mt-20">
        <Checkbox
          active={onlyEstablishment}
          onClick={() => setOnlyEstablishment(!onlyEstablishment)}
          text="Chercher uniquement dans mon établissement"
        />
      </div>

      <div className="mt-20 -mx-40">
        <ListItem isEditable isActive={value === null} onClick={() => select(null)}>
          <PreventionActionResponsibleButton placeholder="Non défini" />
        </ListItem>

        {filteredUsers.map((user) => (
          <ListItem key={user.id} isEditable isActive={value === user.id} onClick={() => select(user.id)}>
            <PreventionActionResponsibleButton user={user} placeholder="Utilisateur sans nom" />
            <Space />
            <div className="my-auto grey-t truncate">{user.email}</div>
          </ListItem>
        ))}

        {filteredUsers.length === 0 && (
          <div className="grey-t px-40 py-20">Aucun utilisateur ne correspond à votre recherche.</div>
        )}
      </div>
    </Modal>
  )
}
